import styles from './EnhancedMeasuresList.module.scss';
import PropTypes from 'prop-types';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import enhancedMeasurePropTypes from '../enhancedMeasurePropTypes';
import { FormattedMessage, useIntl } from 'react-intl';
import Accordion from 'client/components/aria/Accordion';
import Button from 'client/components/aria/Button/Button';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import splitEnhancedMeasures from './splitEnhancedMeasures';
import InfoBox from 'client/pages/MapPage/components/InfoBox/InfoBox';
import { useEnhancedMeasuresAccordions } from './useEnhancedMeasuresAccordions';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import {
    setSupplementaryData,
    setSupplementaryDataAssetError,
    setSupplementaryDataError
} from 'client/store/SupplimentaryDataSlice/supplementaryDataSlice';
import { useSupplementaryDataFetch } from 'client/utils/hooks/useSupplementaryData';
import { FeaturesService } from 'client/services';
import { NavLink } from 'react-router';
import { CONTACT_PAGE } from 'client/utils/routes/routeConstants';

const EnhancedMeasureList = ({ enhancedMeasures, ...props }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const globalError = useSelector(state => state.supplementaryData?.error);
    const loadingAssets = useSelector(state => state.supplementaryData?.loading, shallowEqual);
    const assetErrors = useSelector(state => state.supplementaryData?.assetErrors, shallowEqual);
    const featureIdList = useSelector(state => state.supplementaryData?.featureIdList, shallowEqual);
    const fetchForAssetIds = useSupplementaryDataFetch();

    const {
        enhancedMeasuresPerAssetOwner,
        allAccordionsClosed,
        setAllAccordionsOpen,
        toggleAllAccordions,
        toggleAccordion,
        isAccordionOpen,
        actorIds
    } = useEnhancedMeasuresAccordions(enhancedMeasures);

    // Get all asset IDs for a given actor group
    const getAssetIdsForGroup = useCallback((actorId) => {
        const assets = enhancedMeasuresPerAssetOwner[actorId] || [];
        return assets
            .map(asset => asset.sensitiveAssetData?.dataproviderassigneduniqueid)
            .filter(Boolean);
    }, [enhancedMeasuresPerAssetOwner]);

    // Get all sensitive asset IDs across all groups
    const allSensitiveAssetIds = useMemo(() => {
        return actorIds.flatMap(id => getAssetIdsForGroup(id));
    }, [actorIds, getAssetIdsForGroup]);

    // Fetch supplementary data for all sensitive assets on initial mount
    // (accordions start open by default, so data is needed immediately)
    useEffect(() => {
        if (allSensitiveAssetIds.length > 0) {
            fetchForAssetIds(allSensitiveAssetIds);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Collapse accordions when a global error occurs
    useEffect(() => {
        if (globalError) {
            setAllAccordionsOpen(false);
        }
    }, [globalError, setAllAccordionsOpen]);

    // Fetch for a single group on accordion expand
    const handleAccordionChange = useCallback((actorId, isOpen) => {
        toggleAccordion(actorId, isOpen);
        if (isOpen) {
            const assetIds = getAssetIdsForGroup(actorId);
            fetchForAssetIds(assetIds);
        }
    }, [toggleAccordion, getAssetIdsForGroup, fetchForAssetIds]);

    // Expand All: fetch for all sensitive assets
    const handleToggleAll = useCallback(() => {
        const shouldOpen = allAccordionsClosed;
        toggleAllAccordions();
        if (shouldOpen) {
            fetchForAssetIds(allSensitiveAssetIds);
        }
    }, [toggleAllAccordions, allAccordionsClosed, fetchForAssetIds, allSensitiveAssetIds]);

    // Check if a group has errors
    const getGroupError = useCallback((actorId) => {
        const assetIds = getAssetIdsForGroup(actorId);
        return assetIds.some(id => assetErrors?.[id]);
    }, [getAssetIdsForGroup, assetErrors]);

    // Check if a group is loading
    const isGroupLoading = useCallback((actorId) => {
        const assetIds = getAssetIdsForGroup(actorId);
        return assetIds.some(id => loadingAssets?.[id]);
    }, [getAssetIdsForGroup, loadingAssets]);

    // Retry failed assets for a group
    const retryGroup = useCallback(async (actorId) => {
        const assetIds = getAssetIdsForGroup(actorId);
        const failedIds = assetIds.filter(id => assetErrors?.[id]);
        if (!failedIds.length) {
            return;
        }
        dispatch(setSupplementaryDataAssetError({ assetIds: failedIds, error: null }));
        await fetchForAssetIds(failedIds);
    }, [getAssetIdsForGroup, assetErrors, dispatch, fetchForAssetIds]);

    // Check if any group has errors (for global error display)
    const hasAnyError = useMemo(() => {
        return !!globalError || Object.keys(assetErrors || {}).length > 0;
    }, [globalError, assetErrors]);

    // Retry: if a global error exists (legacy path), retry all featureIdList.
    // Otherwise, retry only per-asset errors.
    const retryAllFailed = useCallback(async () => {
        // If there's a global error, use the legacy retry path — but scope the refetch to only
        // this panel's sensitive assets, not the entire AOI feature list (which may be much larger)
        if (globalError) {
            dispatch(setSupplementaryDataError(null));
            setAllAccordionsOpen(true);
            const seenIds = new Set();
            const dataProviderList = (featureIdList || []).filter(item => {
                if (!allSensitiveAssetIds.includes(item?.dataProviderAssignedUniqueId) || seenIds.has(item?.dataProviderAssignedUniqueId)) {
                    return false;
                }
                seenIds.add(item?.dataProviderAssignedUniqueId);
                return true;
            });
            try {
                const result = await FeaturesService.getSupplementaryData(dataProviderList);
                dispatch(setSupplementaryData(result));
            } catch (e) {
                dispatch(setSupplementaryDataError(e));
            }
            return;
        }
        const failedIds = Object.keys(assetErrors || {});
        if (!failedIds.length) {
            return;
        }
        setAllAccordionsOpen(true);
        dispatch(setSupplementaryDataAssetError({ assetIds: failedIds, error: null }));
        await fetchForAssetIds(failedIds);
    }, [globalError, assetErrors, dispatch, fetchForAssetIds, setAllAccordionsOpen, featureIdList, allSensitiveAssetIds]);

    return (
        <div className={styles.container} data-testid={props['data-testid']}>
            <div className={styles.content} data-testid={`${props['data-testid']}.content`}>
                <p className='govuk-!-padding-2' data-testid="enhancedMeasures.list.comply">
                    <FormattedMessage id='enhancedMeasures.list.comply' />
                </p>
                {hasAnyError
                    ? <div className={styles.errorState} data-testid='supplementaryDataError'>
                        <p>
                            <FormattedMessage
                                id='enhancedMeasures.contactDetailsError'
                                values={{
                                    strong: (chunks) => <strong>{chunks}</strong>,
                                    serviceDesk: chunks => <NavLink className={styles.contactLink} to={CONTACT_PAGE}>{chunks}</NavLink>
                                }}
                            />
                        </p>
                        <Button
                            variant='default'
                            data-testid='supplementaryDataErrorRetryButton'
                            className={styles.contactButton}
                            onPress={retryAllFailed}>
                            <FormattedMessage
                                id='enhancedMeasures.contactDetailsError.button'
                            />
                        </Button>
                    </div>
                    : null
                }
                <Button variant='link' className={styles.collapseAll} onPress={handleToggleAll} data-testid='enhancedMeasures.list.toggleAllButton'>
                    <Icon
                        path={allAccordionsClosed ? mdiChevronDown : mdiChevronUp}
                        size={1.1}
                    />
                    <FormattedMessage id={allAccordionsClosed ? 'enhancedMeasures.list.expand' : 'enhancedMeasures.list.collapse'} />
                </Button>
                {actorIds.map(id => {
                    const assets = enhancedMeasuresPerAssetOwner[id];
                    const actor = assets[0]?.nuarActor;
                    const groupHasError = getGroupError(id) || !!globalError;
                    const groupLoading = isGroupLoading(id);
                    const title = <div className={styles.titleContainer}>
                        <h3 className="govuk-heading-m">{actor?.label || (groupHasError ? intl.formatMessage({ id: 'enhancedMeasures.list.actor.error' }) : intl.formatMessage({ id: 'enhancedMeasures.list.actor.loading' }))}</h3>
                        <div className={`${styles.count} ${assets.length >= 100 ? styles.large : ''}`}>{assets.length}</div>
                    </div>;

                    const {
                        permissionRequiredAssets,
                        furtherConsultationAssets,
                        specialGuidanceAssets,
                        proceedWithCautionAssets
                    } = splitEnhancedMeasures(assets);

                    const isSelected = isAccordionOpen(id);
                    return <div key={id} className={`${styles.item}`} data-testid={props['data-testid'] + '.' + id}>
                        <Accordion
                            sticky
                            isSelected={isSelected}
                            onChange={(isOpen) => handleAccordionChange(id, isOpen)}
                            title={title}
                            titleClassName={`${styles.title} govuk-!-padding-4 govuk-!-padding-top-2`}
                            numberAboveFold={0}
                        >
                            <div className='govuk-!-padding-2'>
                                {groupHasError &&
                                    <div className={styles.errorState} data-testid={`supplementaryDataError-${id}`}>
                                        <p>
                                            <FormattedMessage id='enhancedMeasures.contactDetailsError.group' defaultMessage='Contact details could not be loaded for this group.' />
                                        </p>
                                        <Button
                                            variant='default'
                                            data-testid={`supplementaryDataErrorRetryButton-${id}`}
                                            className={styles.contactButton}
                                            onPress={() => retryGroup(id)}>
                                            <FormattedMessage id='enhancedMeasures.contactDetailsError.button' />
                                        </Button>
                                    </div>
                                }
                                {groupLoading &&
                                    <p data-testid={`supplementaryDataLoading-${id}`}>
                                        <FormattedMessage id='enhancedMeasures.list.actor.loading' />
                                    </p>
                                }
                                {permissionRequiredAssets.length > 0 &&
                                    <InfoBox
                                        data-testid={`permissionRequiredInfoBox-${id}`}
                                        variant='permissionRequired'
                                        assets={permissionRequiredAssets}
                                    />
                                }

                                {furtherConsultationAssets.length > 0 &&
                                    <InfoBox
                                        data-testid={`furtherConsultationInfoBox-${id}`}
                                        variant='furtherConsultation'
                                        assets={furtherConsultationAssets}
                                    />
                                }

                                {proceedWithCautionAssets.length > 0 &&
                                    <InfoBox
                                        data-testid={`proceedWithCautionInfoBox-${id}`}
                                        variant='proceedWithCaution'
                                        assets={proceedWithCautionAssets}
                                    />
                                }

                                {specialGuidanceAssets.length > 0 &&
                                    <InfoBox
                                        data-testid={`specialGuidanceInfoBox-${id}`}
                                        variant='specialGuidance'
                                        assets={specialGuidanceAssets}
                                    />
                                }

                            </div>
                        </Accordion>
                    </div>;
                })}
            </div>
        </div>
    );
};

EnhancedMeasureList.propTypes = {
    ...defaultAriaProps,
    enhancedMeasures: PropTypes.arrayOf(PropTypes.shape(enhancedMeasurePropTypes)),
    'data-testid': PropTypes.string
};

EnhancedMeasureList.defaultProps = {
    enhancedMeasures: [],
    'data-testid': ''
};

export default EnhancedMeasureList;
